import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../utils/api';
import { MessageCircle, ChevronDown, ChevronRight, RefreshCw, Send, Eye, Paperclip, Image as ImageIcon, Check, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

// Palette (design_guidelines.json)
const C = { primary: '#4A6B5D', accent: '#C28E72', border: '#E7E5E4', surface: '#FFFFFF', surfaceHover: '#F5F5F4', bg: '#FAFAF9', text: '#1C1917', textSec: '#78716C', sentBg: '#E3EDE8', recvBg: '#F5F5F4' };

// Formattazione WhatsApp: *grassetto* _corsivo_ ~barrato~
const WA_TOKEN = /(\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~)/g;
const renderWa = (text) => {
  if (text === null || text === undefined) return null;
  return String(text).split(WA_TOKEN).map((p, i) => {
    if (!p) return null;
    if (p.length > 1 && p.startsWith('*') && p.endsWith('*')) return <strong key={i}>{p.slice(1, -1)}</strong>;
    if (p.length > 1 && p.startsWith('_') && p.endsWith('_')) return <em key={i}>{p.slice(1, -1)}</em>;
    if (p.length > 1 && p.startsWith('~') && p.endsWith('~')) return <span key={i} style={{ textDecoration: 'line-through' }}>{p.slice(1, -1)}</span>;
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
};

/**
 * Cronologia WhatsApp di un numero di telefono.
 * Polling stabile ogni 30s. Mostra ultimi 25 messaggi con scroll automatico in fondo.
 *
 * Props:
 *   phone - numero del cliente
 *   onNewMessages - callback (opzionale) invocato con l'unread count
 *   compact - default true (collassato all'inizio)
 *   autoMarkRead - default false
 */
export default function WhatsAppConversation({ phone, onNewMessages, compact = true, autoMarkRead = false }) {
  const [open, setOpen] = useState(!compact);
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [chatId, setChatId] = useState('');
  const [hasError, setHasError] = useState(false);

  // Ref stabile per onNewMessages (evita loop infiniti se il parent la ricrea ogni render)
  const onNewMessagesRef = useRef(onNewMessages);
  useEffect(() => { onNewMessagesRef.current = onNewMessages; }, [onNewMessages]);

  // Ref per scroll automatico in fondo
  const scrollRef = useRef(null);
  const wasAtBottomRef = useRef(true);

  // fetch stabile: dipende SOLO da phone
  const fetchConv = useCallback(async (silent = false) => {
    if (!phone) return;
    if (!silent) setLoading(true);
    try {
      const r = await api.get(`/whatsapp/conversation-by-phone?phone=${encodeURIComponent(phone)}&limit=25`);
      const newMsgs = r.data.messages || [];
      setMessages(prev => {
        // Evita re-render inutili se i messaggi sono gli stessi
        if (prev.length === newMsgs.length && prev[prev.length - 1]?.id === newMsgs[newMsgs.length - 1]?.id) {
          return prev;
        }
        return newMsgs;
      });
      setUnread(r.data.unread_count || 0);
      setChatId(r.data.chat_id || '');
      setHasError(false);
      if (onNewMessagesRef.current) onNewMessagesRef.current(r.data.unread_count || 0);
    } catch (e) {
      console.error('WA conv fetch:', e);
      setHasError(true);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [phone]);

  // Initial fetch + polling stabile ogni 30s
  useEffect(() => {
    fetchConv();
    const iv = setInterval(() => fetchConv(true), 30000);
    return () => clearInterval(iv);
  }, [fetchConv]);

  // Mark read quando si apre (solo una volta)
  useEffect(() => {
    if (open && autoMarkRead && unread > 0 && phone) {
      api.post(`/whatsapp/conversation-by-phone/mark-read?phone=${encodeURIComponent(phone)}`)
        .then(() => {
          setUnread(0);
          if (onNewMessagesRef.current) onNewMessagesRef.current(0);
        })
        .catch((e) => console.error('mark-read:', e));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, autoMarkRead, phone]);

  // Auto-scroll in fondo quando arrivano nuovi messaggi (solo se l'utente era già in fondo)
  useEffect(() => {
    if (!scrollRef.current || messages.length === 0) return;
    if (wasAtBottomRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  const handleScroll = (e) => {
    const el = e.target;
    wasAtBottomRef.current = (el.scrollHeight - el.scrollTop - el.clientHeight) < 50;
  };

  const markRead = async () => {
    if (!phone) return;
    try {
      await api.post(`/whatsapp/conversation-by-phone/mark-read?phone=${encodeURIComponent(phone)}`);
      setUnread(0);
      if (onNewMessagesRef.current) onNewMessagesRef.current(0);
      toast.success('Conversazione marcata come letta');
    } catch (e) {
      console.error('mark-read:', e);
      toast.error('Errore');
    }
  };

  const sendReply = async () => {
    if (!reply.trim() || !chatId) return;
    setSending(true);
    try {
      await api.post('/whatsapp/send', { numero: chatId, messaggio: reply.trim() });
      toast.success('Risposta inviata');
      setReply('');
      await markRead();
      setTimeout(() => fetchConv(true), 1200);
    } catch (e) {
      console.error('send reply:', e);
      toast.error(e.response?.data?.detail || 'Errore invio');
    } finally { setSending(false); }
  };

  if (!phone) return null;

  const formatTime = (ts) => {
    if (!ts) return '';
    try {
      const d = new Date(Number(ts) > 1e12 ? Number(ts) : Number(ts) * 1000);
      return d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  return (
    <div className="rounded-xl p-3" style={{ border: `1px solid ${C.border}`, backgroundColor: C.bg }} data-testid="wa-conversation-box">
      <div className="flex items-center justify-between">
        <button onClick={() => setOpen(!open)} className="flex items-center gap-2 text-sm font-semibold" style={{ color: C.text, fontFamily: 'Outfit, sans-serif' }}>
          <MessageCircle size={14} style={{ color: C.primary }} /> Conversazione WhatsApp
          {unread > 0 && (
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-white text-[10px] font-bold animate-pulse" style={{ backgroundColor: C.accent }} data-testid="wa-unread-badge">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
          {messages.length > 0 && <span className="text-[10px]" style={{ color: C.textSec }}>({messages.length})</span>}
          {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        </button>
        <div className="flex gap-1">
          {unread > 0 && (
            <Button size="sm" variant="ghost" onClick={markRead} className="text-[11px] h-7" data-testid="wa-mark-read">
              <Eye size={11} className="mr-1" /> Letto
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => fetchConv(false)} disabled={loading} className="text-[11px] h-7" title="Aggiorna">
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
      </div>

      {open && (
        <div className="mt-2 space-y-2">
          {loading && messages.length === 0 && (
            <div className="space-y-2 py-2" data-testid="wa-conv-skeleton">
              {[0, 1, 2].map(i => (
                <div key={i} className={`flex ${i % 2 ? 'justify-end' : 'justify-start'}`}>
                  <div className="h-7 rounded-lg animate-pulse" style={{ width: `${45 + (i * 13) % 35}%`, backgroundColor: i % 2 ? C.sentBg : C.recvBg }} />
                </div>
              ))}
            </div>
          )}
          {!loading && messages.length === 0 && !hasError && (
            <p className="text-[11px] italic text-center py-3" style={{ color: C.textSec }}>
              Nessun messaggio WhatsApp con questo numero. Le risposte appariranno qui.
            </p>
          )}
          {hasError && messages.length === 0 && (
            <div className="flex items-center justify-center gap-1.5 py-3">
              <AlertCircle size={13} className="text-red-500" />
              <p className="text-[11px] italic text-red-500">
                WhatsApp non raggiungibile. Verifica che il servizio sia attivo.
              </p>
            </div>
          )}
          {messages.length > 0 && (
            <div
              ref={scrollRef}
              onScroll={handleScroll}
              className="max-h-72 overflow-y-auto space-y-1.5 rounded-lg p-2 scroll-smooth"
              style={{ backgroundColor: C.surface, border: `1px solid ${C.border}` }}
            >
              {messages.map((m, i) => (
                <div key={m.id || `msg-${i}`} className={`flex ${m.fromMe ? 'justify-end' : 'justify-start'}`}>
                  <div className="max-w-[80%] rounded-lg px-2.5 py-1.5 text-[11px] shadow-sm" style={{ backgroundColor: m.fromMe ? C.sentBg : C.recvBg, color: C.text }}>
                    {m.type === 'image' && (
                      <div className="flex items-center gap-1 text-[10px] italic mb-1" style={{ color: C.textSec }}><ImageIcon size={11} /> Immagine</div>
                    )}
                    {m.hasMedia && m.type !== 'image' && (
                      <div className="flex items-center gap-1 text-[10px] italic mb-1" style={{ color: C.textSec }}><Paperclip size={11} /> Allegato</div>
                    )}
                    {m.body ? (
                      <p className="whitespace-pre-wrap break-words">{renderWa(m.body)}</p>
                    ) : (m.hasMedia ? null : <p className="italic" style={{ color: '#A8A29E' }}>[messaggio vuoto]</p>)}
                    <div className="flex items-center justify-end gap-1 mt-0.5 text-[9px]" style={{ color: C.textSec }}>
                      <span>{formatTime(m.timestamp)}</span>
                      {m.fromMe && <Check size={11} style={{ color: C.primary }} />}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {chatId && (
            <div className="flex gap-1">
              <input
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                placeholder="Rispondi al cliente..."
                className="flex-1 text-[12px] px-2 py-1.5 rounded-md outline-none"
                style={{ border: `1px solid ${C.border}`, color: C.text }}
                data-testid="wa-quick-reply"
              />
              <Button size="sm" onClick={sendReply} disabled={sending || !reply.trim()} className="text-white h-8" style={{ backgroundColor: C.primary }} data-testid="wa-send-reply">
                {sending ? <RefreshCw size={12} className="animate-spin" /> : <Send size={12} />}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
